import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react"

export const MENU_GAP_PX = 4
const EDGE_GUTTER_PX = 8
/** Выше shot-panel Dialog (z-101) и внутренних оверлеев панели. */
export const MENU_Z_INDEX = 200

/** Portal внутрь dialog, иначе modal Dialog режет pointer-events у body-порталов. */
export function portalRootFor(anchor: HTMLElement | null): HTMLElement {
  if (typeof document === "undefined") {
    throw new Error("portalRootFor requires a browser document")
  }
  const host = anchor?.closest('[data-workspace-dialog], [role="dialog"], [role="alertdialog"]') ?? null
  return host instanceof HTMLElement ? host : document.body
}

function cssValueCreatesContainingBlock(value: string): boolean {
  return value !== "" && value !== "none"
}

/**
 * `position: fixed` is laid out against this element when it has a transform-like
 * property or paint containment. A dialog that is only shifted with `top` is not.
 */
function establishesFixedContainingBlock(el: HTMLElement): boolean {
  const style = getComputedStyle(el)
  if (cssValueCreatesContainingBlock(style.transform)) return true
  if (cssValueCreatesContainingBlock(style.translate)) return true
  if (cssValueCreatesContainingBlock(style.scale)) return true
  if (cssValueCreatesContainingBlock(style.rotate)) return true
  if (cssValueCreatesContainingBlock(style.filter)) return true
  if (cssValueCreatesContainingBlock(style.backdropFilter)) return true
  if (cssValueCreatesContainingBlock(style.perspective)) return true
  const contain = style.contain
  return contain === "strict" || contain === "content" || contain.split(/\s+/).includes("paint")
}

/** Subtract the portal box only when fixed is positioned against that portal, not the viewport. */
export function fixedPosInPortal(
  viewport: { top: number; left: number },
  portalRoot: HTMLElement | null,
): { top: number; left: number } {
  if (!portalRoot || portalRoot === document.body || !establishesFixedContainingBlock(portalRoot)) return viewport
  const frame = portalRoot.getBoundingClientRect()
  return { top: viewport.top - frame.top, left: viewport.left - frame.left }
}

type FitContentWidthOptions = {
  enabled: boolean
  labels: string[]
  /** Ширина шеврона, паддингов и бордера кнопки. */
  chromePx: number
  anchorRef: RefObject<HTMLElement | null>
  /** Скрытый span для замера, когда canvas недоступен. */
  probeRef?: RefObject<HTMLElement | null>
}

/** Ширина кнопки по самой длинной опции: замер шрифтом самой кнопки, иначе text-xs врёт. */
export function useFitContentWidth({
  enabled,
  labels,
  chromePx,
  anchorRef,
  probeRef,
}: FitContentWidthOptions): number | null {
  const [widthPx, setWidthPx] = useState<number | null>(null)
  const labelsKey = labels.join("\u0000")

  useLayoutEffect(() => {
    if (!enabled || labels.length === 0) {
      setWidthPx(null)
      return
    }

    const anchor = anchorRef.current
    const font = anchor ? getComputedStyle(anchor).font : "12px ui-sans-serif, system-ui, sans-serif"
    const ctx = document.createElement("canvas").getContext("2d")
    let maxLabelPx = 0
    if (ctx) {
      ctx.font = font
      for (const label of labels) {
        maxLabelPx = Math.max(maxLabelPx, ctx.measureText(label).width)
      }
    } else {
      const probe = probeRef?.current
      if (!probe) return
      for (const label of labels) {
        probe.textContent = label
        maxLabelPx = Math.max(maxLabelPx, probe.offsetWidth)
      }
      probe.textContent = ""
    }
    setWidthPx(Math.ceil(maxLabelPx + chromePx))
    // labelsKey заменяет нестабильный по ссылке массив labels
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, labelsKey, chromePx, anchorRef, probeRef])

  return widthPx
}

/**
 * `up` / `down` — меню под/над кнопкой, `align` задаёт горизонтальную привязку.
 * `left` / `right` — flyout сбоку от кнопки, верх меню на уровне верха кнопки; `align` не используется.
 */
export type DropdownMenuDirection = "up" | "down" | "left" | "right"

type DropdownMenuOptions = {
  open: boolean
  disabled?: boolean
  /** Preferred open side; flips when that side has too little room. */
  direction?: DropdownMenuDirection
  /** Горизонтальная привязка меню к кнопке: по правому краю (по умолчанию), по левому или по центру кнопки. */
  align?: "left" | "right" | "center"
  /** Не переключать привязку на другой край и не сдвигать меню, если оно шире места справа. */
  pinAlign?: boolean
  anchorRef: RefObject<HTMLElement | null>
  menuRef: RefObject<HTMLElement | null>
  rootRef: RefObject<HTMLElement | null>
  /** Ширина меню от прямоугольника кнопки; остальное позиционирование общее. */
  resolveWidth: (anchorRect: DOMRect) => Pick<CSSProperties, "minWidth" | "width" | "maxWidth">
  /** Пересчёт зависимых от меню оверлеев (скролл/ресайз). */
  onReposition?: () => void
  onOutsideClick: (event: MouseEvent) => void
  /** Значения, при смене которых меню надо переразместить. */
  positionKey?: unknown
  /** Оценка высоты до замера DOM (обычно совпадает с max-h-* меню). */
  estimatedMenuHeightPx?: number
  /** Оценка ширины до замера DOM (для горизонтального flip). */
  estimatedMenuWidthPx?: number
  /**
   * Portal to document.body (viewport coords). Use when a body-level overlay
   * (e.g. 3D scene stage) sits above the dialog stacking context.
   */
  portalToBody?: boolean
  zIndex?: number
}

const DEFAULT_ESTIMATED_MENU_HEIGHT_PX = 240
const DEFAULT_ESTIMATED_MENU_WIDTH_PX = 192

function spaceAroundAnchor(rect: DOMRect, frame: DOMRect | null) {
  const top = frame ? frame.top : 0
  const bottom = frame ? frame.bottom : window.innerHeight
  const left = frame ? frame.left : 0
  const right = frame ? frame.right : window.innerWidth
  return {
    above: rect.top - top - MENU_GAP_PX,
    below: bottom - rect.bottom - MENU_GAP_PX,
    /** Место справа от левого края якоря (меню с `left`, растёт вправо). */
    towardRight: right - rect.left - MENU_GAP_PX,
    /** Место слева от правого края якоря (меню с `right`, растёт влево). */
    towardLeft: rect.right - left - MENU_GAP_PX,
    /** Место справа от правого края якоря (flyout вправо). */
    besideRight: right - rect.right - MENU_GAP_PX,
    /** Место слева от левого края якоря (flyout влево). */
    besideLeft: rect.left - left - MENU_GAP_PX,
  }
}

function resolveOpenSide(
  preferred: "left" | "right",
  space: { besideRight: number; besideLeft: number },
  menuWidth: number,
): "left" | "right" {
  if (preferred === "right") {
    if (space.besideRight >= menuWidth || space.besideRight >= space.besideLeft) return "right"
    return "left"
  }
  if (space.besideLeft >= menuWidth || space.besideLeft >= space.besideRight) return "left"
  return "right"
}

function resolveOpenDirection(
  preferred: "up" | "down",
  space: { above: number; below: number },
  menuHeight: number,
): "up" | "down" {
  if (preferred === "down") {
    if (space.below >= menuHeight || space.below >= space.above) return "down"
    return "up"
  }
  if (space.above >= menuHeight || space.above >= space.below) return "up"
  return "down"
}

function resolveAlign(
  preferred: "left" | "right",
  space: { towardRight: number; towardLeft: number },
  menuWidth: number,
): "left" | "right" {
  if (preferred === "left") {
    if (space.towardRight >= menuWidth || space.towardRight >= space.towardLeft) return "left"
    return "right"
  }
  if (space.towardLeft >= menuWidth || space.towardLeft >= space.towardRight) return "right"
  return "left"
}

/**
 * Общая механика выпадающего меню в портале: fixed-позиция от кнопки,
 * пересчёт на scroll/resize и закрытие по клику снаружи.
 *
 * Если портал внутри dialog с transform (WorkspaceDialog и т.п.), fixed
 * привязан к dialog, а не к viewport — координаты пересчитываем в его систему.
 *
 * Если preferred-сторона не влезает, меню открывается в другую (и по вертикали, и по горизонтали).
 *
 * Позиция в useLayoutEffect: сначала меню монтируется hidden (замер DOM), затем
 * до paint — финальная позиция. Без rAF-вспышки «не туда → прыжок».
 */
export function useDropdownMenu({
  open,
  disabled = false,
  direction = "down",
  align = "right",
  pinAlign = false,
  anchorRef,
  menuRef,
  rootRef,
  resolveWidth,
  onReposition,
  onOutsideClick,
  positionKey,
  estimatedMenuHeightPx = DEFAULT_ESTIMATED_MENU_HEIGHT_PX,
  estimatedMenuWidthPx = DEFAULT_ESTIMATED_MENU_WIDTH_PX,
  portalToBody = false,
  zIndex = MENU_Z_INDEX,
}: DropdownMenuOptions): { menuStyle: CSSProperties | null; portalRoot: HTMLElement | null } {
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null)
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null)
  const callbacksRef = useRef({ resolveWidth, onReposition, onOutsideClick })
  callbacksRef.current = { resolveWidth, onReposition, onOutsideClick }

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuStyle(null)
      setPortalRoot(null)
      return
    }

    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const root = portalToBody
        ? typeof document !== "undefined"
          ? document.body
          : portalRootFor(anchor)
        : portalRootFor(anchor)
      // Only a transformed (or paint-contained) dialog is the fixed containing block.
      const frame =
        root !== document.body && establishesFixedContainingBlock(root) ? root.getBoundingClientRect() : null
      const measuredBox = menuRef.current?.getBoundingClientRect()
      // Портал ещё не в DOM — монтируем hidden; следующий layout-pass (portalRoot в deps) замерит.
      if (!measuredBox || measuredBox.width <= 0 || measuredBox.height <= 0) {
        setPortalRoot(root)
        setMenuStyle({
          position: "fixed",
          zIndex,
          visibility: "hidden",
          pointerEvents: "none",
          top: 0,
          left: 0,
          ...callbacksRef.current.resolveWidth(rect),
        })
        return
      }

      const space = spaceAroundAnchor(rect, frame)
      // Высоту берём по содержимому: коробку уже ужал наш же maxHeight (и max-h-*
      // на самом меню), и решение «влезает / не влезает» начало бы скакать на
      // каждом пересчёте — меню мигало бы, вылезая за край диалога.
      // scrollHeight не включает рамки меню — добавляем их, иначе в полосе в пару
      // пикселей решение «ограничить высоту» переключалось бы на каждом скролле.
      const menuEl = menuRef.current
      const borderY = menuEl ? menuEl.offsetHeight - menuEl.clientHeight : 0
      const naturalHeight = Math.max(measuredBox.height, (menuEl?.scrollHeight ?? 0) + borderY)
      const menuHeight = naturalHeight > 0 ? naturalHeight : estimatedMenuHeightPx
      const menuWidth = measuredBox.width > 0 ? measuredBox.width : estimatedMenuWidthPx

      const next: CSSProperties = {
        position: "fixed",
        zIndex,
        // Modal Dialog sets body pointer-events:none — body-portaled menus need this.
        pointerEvents: "auto",
        ...callbacksRef.current.resolveWidth(rect),
      }

      if (direction === "left" || direction === "right") {
        // Flyout сбоку: верх меню на уровне верха кнопки; если снизу не хватает —
        // сдвигаем вверх, а совсем длинный список ограничиваем по высоте.
        const openSide = resolveOpenSide(direction, space, menuWidth)
        const frameTop = frame ? frame.top : 0
        const frameBottom = frame ? frame.bottom : window.innerHeight
        const maxHeight = frameBottom - frameTop - MENU_GAP_PX * 2
        const height = Math.min(menuHeight, Math.max(96, maxHeight))
        if (menuHeight > maxHeight) next.maxHeight = Math.max(96, maxHeight)
        const topViewport = Math.max(frameTop + MENU_GAP_PX, Math.min(rect.top, frameBottom - MENU_GAP_PX - height))
        next.top = frame ? topViewport - frame.top : topViewport
        if (openSide === "right") {
          next.left = frame ? rect.right - frame.left + MENU_GAP_PX : rect.right + MENU_GAP_PX
        } else {
          next.right = frame ? frame.right - rect.left + MENU_GAP_PX : window.innerWidth - rect.left + MENU_GAP_PX
        }
        setMenuStyle(next)
        setPortalRoot(root)
        callbacksRef.current.onReposition?.()
        return
      }

      const openDirection = resolveOpenDirection(direction, space, menuHeight)
      const openAlign = align === "center" || pinAlign ? align : resolveAlign(align, space, menuWidth)
      const available = openDirection === "down" ? space.below : space.above
      // Если места мало — ограничиваем высоту, длинный список скроллится внутри.
      if (available < menuHeight) {
        next.maxHeight = Math.max(96, available)
      }
      if (openAlign === "center") {
        const frameLeft = frame ? frame.left : 0
        const frameRight = frame ? frame.right : window.innerWidth
        const anchorMid = rect.left + rect.width / 2
        const rawLeft = anchorMid - menuWidth / 2
        const leftViewport = Math.max(frameLeft, Math.min(rawLeft, frameRight - EDGE_GUTTER_PX - menuWidth))
        next.left = frame ? leftViewport - frame.left : leftViewport
      } else if (openAlign === "left") {
        const frameLeft = frame ? frame.left : 0
        const frameRight = frame ? frame.right : window.innerWidth
        const leftViewport = pinAlign
          ? Math.max(frameLeft, rect.left)
          : Math.max(frameLeft, Math.min(rect.left, frameRight - EDGE_GUTTER_PX - menuWidth))
        next.left = frame ? leftViewport - frame.left : leftViewport
      } else {
        next.right = frame ? frame.right - rect.right : window.innerWidth - rect.right
      }
      if (openDirection === "up") {
        next.bottom = frame ? frame.bottom - rect.top + MENU_GAP_PX : window.innerHeight - rect.top + MENU_GAP_PX
      } else {
        next.top = frame ? rect.bottom - frame.top + MENU_GAP_PX : rect.bottom + MENU_GAP_PX
      }
      setMenuStyle(next)
      setPortalRoot(root)
      callbacksRef.current.onReposition?.()
    }

    // Скролл внутри самого меню кнопку не двигает — пересчитывать нечего,
    // а перерисовка на каждый кадр колеса заметно тормозит длинный список.
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return
      update()
    }

    update()
    window.addEventListener("resize", update)
    // capture: скролл любого предка (overflow-x таймлайна) тоже сдвигает кнопку.
    window.addEventListener("scroll", onScroll, true)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", onScroll, true)
    }
  }, [
    open,
    disabled,
    direction,
    align,
    pinAlign,
    anchorRef,
    menuRef,
    portalRoot,
    positionKey,
    estimatedMenuHeightPx,
    estimatedMenuWidthPx,
    portalToBody,
    zIndex,
  ])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return
      // Nested portaled pickers (select listbox, date dialog) sit outside menuRef.
      if (target instanceof Element && target.closest('[role="listbox"], [data-panel-wheel-isolate]')) return
      callbacksRef.current.onOutsideClick(e)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open, rootRef, menuRef])

  return { menuStyle, portalRoot }
}
